import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  TextField,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import DialogTitle from '../shared/DialogTitle';
import Loading from '../shared/Loading';
import ServerError from '../error/ServerError';
import {
  GENERATE_MEAL_PLAN_FOR_CLIENT,
  GET_NUTRITION_PREFERENCES,
} from '../../graphql/nutrition';

const recommendedForm = {
  title: '',
  calories: 2000,
  proteinGrams: 150,
  carbGrams: 200,
  fatGrams: 67,
  mealsPerDay: 3,
  snacksPerDay: 0,
  dietType: 'default',
  allergies: 'none',
  startedAt: '',
};

const getTodayDateString = () => new Date().toISOString().split('T')[0];

const getRecommendedForm = () => ({
  ...recommendedForm,
  startedAt: getTodayDateString(),
});

const toNumberOrNull = (value) => {
  if (value === '' || value == null) {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toCsvArray = (value) =>
  (value || '')
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => !/^none$/i.test(entry))
    .filter(Boolean);

const getDefaultMealPlanTitle = (user) =>
  `Meal for ${user?.name || user?.email || 'customer'}`;

export default function GenerateMealPlanDialog({ open, onClose, user }) {
  const { enqueueSnackbar } = useSnackbar();
  const [form, setForm] = useState(getRecommendedForm);

  const { loading, error, data } = useQuery(GET_NUTRITION_PREFERENCES, {
    variables: { input: { userId: user?._id } },
    skip: !open || !user?._id,
  });

  const [generateMealPlanForClient, { loading: saving }] = useMutation(
    GENERATE_MEAL_PLAN_FOR_CLIENT,
    {
      onError: (error) => {
        enqueueSnackbar(`Error: ${error.message}`, { variant: 'error' });
      },
      onCompleted: (data) => {
        enqueueSnackbar(
          'Meal plan generated and assigned. Member notification will be sent.',
          {
            variant: 'success',
          }
        );
        onClose(data?.generateMealPlanForClient);
      },
    }
  );

  useEffect(() => {
    if (!open) {
      setForm(getRecommendedForm());
      return;
    }

    const defaults = {
      ...getRecommendedForm(),
      title: getDefaultMealPlanTitle(user),
    };
    const preferences = data?.getNutritionPreferences;
    if (!preferences) {
      setForm(defaults);
      return;
    }

    setForm({
      ...defaults,
      calories: preferences.macroNutrientsPerDay?.calories ?? defaults.calories,
      proteinGrams:
        preferences.macroNutrientsPerDay?.proteinGrams ?? defaults.proteinGrams,
      carbGrams:
        preferences.macroNutrientsPerDay?.carbGrams ?? defaults.carbGrams,
      fatGrams: preferences.macroNutrientsPerDay?.fatGrams ?? defaults.fatGrams,
      mealsPerDay: preferences.mealsPerDay ?? defaults.mealsPerDay,
      snacksPerDay: preferences.snacksPerDay ?? defaults.snacksPerDay,
      dietType: preferences.dietType ?? defaults.dietType,
      allergies:
        preferences.allergies?.length > 0
          ? preferences.allergies.join(', ')
          : defaults.allergies,
    });
  }, [data, open]);

  const handleChange = (event) => {
    setForm({ ...form, [event.target.name]: event.target.value });
  };

  const handleSubmit = async () => {
    const macros = {
      calories: toNumberOrNull(form.calories),
      proteinGrams: toNumberOrNull(form.proteinGrams),
      carbGrams: toNumberOrNull(form.carbGrams),
      fatGrams: toNumberOrNull(form.fatGrams),
    };

    if (!macros.calories || macros.calories <= 0) {
      enqueueSnackbar('Calories are required.', { variant: 'warning' });
      return;
    }

    await generateMealPlanForClient({
      variables: {
        input: {
          userId: user._id,
          title: form.title?.trim() || getDefaultMealPlanTitle(user),
          macros,
          startedAt: form.startedAt || null,
          nutritionPreferences: {
            userId: user._id,
            allergies: toCsvArray(form.allergies),
            mealsPerDay: toNumberOrNull(form.mealsPerDay),
            snacksPerDay: toNumberOrNull(form.snacksPerDay),
            dietType: form.dietType || null,
            macroNutrientsPerDay: macros,
          },
        },
      },
    });
  };

  if (error) {
    return <ServerError message={error.message || 'Load failed'} />;
  }

  return (
    <Dialog onClose={() => onClose()} open={open} fullWidth maxWidth="md">
      <DialogTitle onClose={() => onClose()}>Generate Meal Plan</DialogTitle>
      <DialogContent dividers>
        {loading && <Loading />}
        {!loading && (
          <Box mt={1}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  name="title"
                  onChange={handleChange}
                  value={form.title}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Calories"
                  name="calories"
                  onChange={handleChange}
                  type="number"
                  value={form.calories}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Protein grams"
                  name="proteinGrams"
                  onChange={handleChange}
                  type="number"
                  value={form.proteinGrams}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Carb grams"
                  name="carbGrams"
                  onChange={handleChange}
                  type="number"
                  value={form.carbGrams}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Fat grams"
                  name="fatGrams"
                  onChange={handleChange}
                  type="number"
                  value={form.fatGrams}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Meals per day"
                  name="mealsPerDay"
                  onChange={handleChange}
                  type="number"
                  value={form.mealsPerDay}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Snacks per day"
                  name="snacksPerDay"
                  onChange={handleChange}
                  type="number"
                  value={form.snacksPerDay}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Diet type"
                  name="dietType"
                  onChange={handleChange}
                  value={form.dietType}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Start date"
                  name="startedAt"
                  onChange={handleChange}
                  type="date"
                  value={form.startedAt}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  helperText="Separate allergies with commas."
                  label="Allergies"
                  name="allergies"
                  onChange={handleChange}
                  value={form.allergies}
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()}>Cancel</Button>
        <Button
          color="primary"
          disabled={saving}
          onClick={handleSubmit}
          variant="contained"
        >
          Generate
        </Button>
      </DialogActions>
    </Dialog>
  );
}
